import React, { useState, useEffect } from 'react';

type Testimonial = {
  quotation: string;
  attribution: string;
  logoUrl?: string | { url?: string; src?: string };
  logoAlt?: string;
};

const srcFromFileInput = (val: any): string => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') return val.url || val.src || '';
  return '';
};

const TestimonialsSegment = ({
  sectionTitle = 'Learn Why Educators Fall in Love with Our Product',
  testimonials = [],
  backgroundColor = '#f8f9fa',

  // Typography props
  headerFontFamily = 'Georgia, serif',
  headerFontWeight = '700',
  headerFontSizeDesktop = '2.5rem',
  headerFontSizeTablet = '2rem',
  headerFontSizeMobile = '1.75rem',
  headerTextColor = '#333',

  quoteFontFamily = 'Georgia, serif',
  quoteFontWeight = '300',
  quoteFontSizeDesktop = '1.25rem',
  quoteFontSizeTablet = '1.125rem',
  quoteFontSizeMobile = '1rem',
  quoteTextColor = '#555',

  attributionFontFamily = 'Arial, sans-serif',
  attributionFontWeight = '600',
  attributionFontSizeDesktop = '1rem',
  attributionFontSizeTablet = '0.9rem',
  attributionFontSizeMobile = '0.85rem',
  attributionTextColor = '#777',

  logoSizeDesktop = '200px',
  logoSizeTablet = '160px',
  logoSizeMobile = '120px',

  // Arrow props per breakpoint
  arrowSizeDesktop = '2rem',
  arrowSizeTablet = '1.75rem',
  arrowSizeMobile = '1.5rem',

  arrowColorDesktop = '#333',
  arrowColorTablet = '#333',
  arrowColorMobile = '#333',

  arrowBackgroundColor = 'rgba(255,255,255,0.9)',

  showActiveLogoOnly = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);

  const validTestimonials = Array.isArray(testimonials) && testimonials.length > 0 ? testimonials : [];

  // Handle navigation
  const nextTestimonial = () => setActiveIndex((prev) => (prev + 1) % validTestimonials.length);

  const prevTestimonial = () =>
    setActiveIndex((prev) => (prev - 1 + validTestimonials.length) % validTestimonials.length);

  const goToTestimonial = (index: number) => {
    if (index !== activeIndex) setActiveIndex(index);
  };

  const activeTestimonial = validTestimonials[activeIndex];

  // Track window size
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Pick arrow size & color based on breakpoint
  const getArrowSize = () => {
    if (windowWidth <= 767) return arrowSizeMobile;
    if (windowWidth <= 1024) return arrowSizeTablet;
    return arrowSizeDesktop;
  };

  const getArrowColor = () => {
    if (windowWidth <= 767) return arrowColorMobile;
    if (windowWidth <= 1024) return arrowColorTablet;
    return arrowColorDesktop;
  };

  const arrowStyleBase: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: arrowBackgroundColor,
    border: 'none',
    fontSize: getArrowSize(),
    color: getArrowColor(),
    padding: '0.5rem 0.75rem',
    borderRadius: '50%',
    cursor: 'pointer',
    zIndex: 10,
    lineHeight: 1,
  };

  // Compute window of logos (always 5, with active centered if possible)
  const getLogoWindow = () => {
    const total = validTestimonials.length;
    if (total <= 4) return validTestimonials.map((_, i) => i);

    const indices: number[] = [];
    for (let i = -2; i <= 2; i++) {
      indices.push((activeIndex + i + total) % total);
    }
    return indices;
  };

  return (
    <section
      style={{
        backgroundColor,
        padding: '6rem 2rem',
        textAlign: 'center',
        overflow: 'hidden',
        position: 'relative',
        width: '100%', // full width
      }}
    >
      {/* Header */}
      <h2
        style={{
          fontFamily: headerFontFamily,
          fontWeight: headerFontWeight,
          color: headerTextColor,
          fontSize: headerFontSizeDesktop,
          marginBottom: '4rem',
          maxWidth: '800px',
          marginLeft: 'auto',
          marginRight: 'auto',
          lineHeight: 1.2,
        }}
      >
        {sectionTitle}
      </h2>

      {/* Quote Section */}
      <div
        style={{
          width: '100%',
          maxWidth: '700px',
          margin: '0 auto 4rem auto',
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          padding: '0 2rem',
          boxSizing: 'border-box',
        }}
      >
        {validTestimonials.length > 1 && (
          <>
            <button
              onClick={prevTestimonial}
              aria-label='Previous testimonial'
              type='button'
              style={{ ...arrowStyleBase, left: '0.5rem' }}
            >
              ←
            </button>

            <button
              onClick={nextTestimonial}
              aria-label='Next testimonial'
              type='button'
              style={{ ...arrowStyleBase, right: '0.5rem' }}
            >
              →
            </button>
          </>
        )}

        <div style={{ width: '100%' }}>
          <blockquote style={{ margin: 0 }}>
            <p
              style={{
                fontFamily: quoteFontFamily,
                fontWeight: quoteFontWeight,
                color: quoteTextColor,
                fontSize: quoteFontSizeDesktop,
                margin: 0,
                lineHeight: 1.6,
                fontStyle: 'italic',
              }}
            >
              “{activeTestimonial?.quotation}”
            </p>
          </blockquote>
          <cite
            style={{
              fontFamily: attributionFontFamily,
              fontWeight: attributionFontWeight,
              color: attributionTextColor,
              fontSize: attributionFontSizeDesktop,
              display: 'block',
              marginTop: '1rem',
              fontStyle: 'normal',
            }}
          >
            — {activeTestimonial?.attribution}
          </cite>
        </div>
      </div>

      {/* Logos Section */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'nowrap',
          maxWidth: '100%',
          margin: '0 auto',
          overflow: 'hidden',
        }}
      >
        {getLogoWindow().map((index) => {
          const logo = validTestimonials[index];
          const isActive = index === activeIndex;
          return (
            <div
              key={index}
              onClick={() => goToTestimonial(index)}
              style={{
                padding: '1rem',
                backgroundColor: isActive ? 'white' : 'transparent',
                border: '1px solid transparent',
                borderRadius: '8px',
                boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                flex: '0 0 auto',
                width: '20%', // 5 logos fit
                maxWidth: '200px',
                transition: 'all 0.3s ease',
                opacity: isActive ? 1 : 0.6,
                transform: isActive ? 'scale(1)' : 'scale(0.9)',
              }}
            >
              <img
                src={srcFromFileInput(logo.logoUrl) || 'https://via.placeholder.com/200x120/f0f0f0/666?text=Logo'}
                alt={logo.logoAlt || `Logo for ${logo.attribution}`}
                style={{
                  width: '100%',
                  maxWidth: logoSizeDesktop,
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src =
                    'https://via.placeholder.com/200x120/f0f0f0/666?text=Logo';
                }}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default TestimonialsSegment;
